import { computed, inject, Injectable, OnDestroy, signal } from '@angular/core';
import { BaseDatasource } from '@shared/base/base.datasource';
import { IProgramTarget, IProgramTargetParams, ProgressTypeFilterValues } from '../interfaces/tab-program-target.interface';
import { ProgramTargetApiService } from './program-target-api.service';

@Injectable()
export class ProgramTargetService implements OnDestroy {
  constructor() {
    this.dataSource.pageSize = 10;
    this.dataSource.pageSizeSelect = [10, 25, 50];
    this.dataSource.disconnect = () => {};
  }

  ngOnDestroy(): void {
    this.dataSource.tableSubject.complete();
  }

  private readonly apiService = inject(ProgramTargetApiService);

  readonly dataSource = new BaseDatasource<IProgramTarget>();

  // search & filter
  readonly keyword = signal<string>('');
  readonly progressType = signal<ProgressTypeFilterValues | undefined>(undefined);
  readonly isFilterActive = computed(() => !!this.keyword() || !!this.progressType());

  readonly isEmptyProgram = signal<boolean>(false);
  readonly isEmptySearchFilterResult = signal(false);

  loadData(distributorId: string): void {
    this.dataSource.isPreloadTextViewedSubject.next(true);
    this.dataSource.isFinishLoadingSubject.next(false);
    this.dataSource.hasItems = false;

    this.isEmptyProgram.set(false);
    this.isEmptySearchFilterResult.set(false);

    const params: IProgramTargetParams = {
      page: this.dataSource.current_page,
      size: this.dataSource.pageSize,
      keyword: this.keyword() || undefined,
      progress_type: this.progressType(),
    };

    this.apiService.getProgramTargetList(distributorId, params).subscribe({
      next: (response) => {
        if (!response) {
          return;
        }

        if (!response.success) {
          this.dataSource.tableSubject.next([]);
          this.dataSource.totalItem$.next(0);
          this.dataSource.hasItems = false;

          this.isEmptyProgram.set(true);
          this.isEmptySearchFilterResult.set(false);

          this.dataSource.isPreloadTextViewedSubject.next(false);
          this.dataSource.isFinishLoadingSubject.next(true);

          return;
        }

        // DATA
        this.dataSource.tableSubject.next(response.data);

        this.dataSource.current_page = response.current_page;
        this.dataSource.totalPage = response.total_page;
        this.dataSource.pageSize = response.page_size;
        this.dataSource.totalItem = response.total_data;
        this.dataSource.totalItem$.next(response.total_data);

        this.dataSource.hasItems = response.data.length > 0;

        this.isEmptyProgram.set(!this.isFilterActive() && response.data.length === 0);
        this.isEmptySearchFilterResult.set(this.isFilterActive() && response.data.length === 0);

        // FINISH LOADING
        this.dataSource.isPreloadTextViewedSubject.next(false);
        this.dataSource.isFinishLoadingSubject.next(true);
      },

      error: () => {
        this.dataSource.tableSubject.next([]);
        this.dataSource.totalItem$.next(0);
        this.dataSource.totalItem = 0;
        this.dataSource.hasItems = false;

        this.isEmptyProgram.set(true);
        this.isEmptySearchFilterResult.set(false);

        this.dataSource.isPreloadTextViewedSubject.next(false);
        this.dataSource.isFinishLoadingSubject.next(true);
      },
    });
  }

  setKeyword(value: string): void {
    this.keyword.set(value);
  }

  setProgressType(value: ProgressTypeFilterValues | undefined): void {
    this.progressType.set(value);
  }
}
